import React, { useState, useCallback } from "react";
import CustomModal from "../../theme/style-components/CustomModal";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  Paper,
  IconButton,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { usePostEmployeeCsvMutation } from "../../store/employee/EmployeeApiSlice";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";
import { selectCurrentUserObject } from "../../store/auth/authSlice";

const ImportCsv = ({ isImportCsv, setIsImportCsv }) => {
  const [file, setFile] = useState(null);
  const [skipExisting, setSkipExisting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [postEmployeeCSV] = usePostEmployeeCsvMutation();
  const theme = useTheme();
  const user = useSelector(selectCurrentUserObject);

  const handleModalClose = () => {
    setIsImportCsv(false);
    // Reset form state when closing
    setFile(null);
    setSkipExisting(false);
    setUpdateExisting(false);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
    } else {
      alert("Please upload a CSV file");
      event.target.value = null;
    }
  };

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "text/csv") {
      setFile(droppedFile);
    } else {
      alert("Please upload a CSV file");
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Handle form submission here
    console.log({
      file,
      skipExisting,
      updateExisting,
    });
    if (file) {
      let payload = new FormData();
      payload.append("org_id", user?.company);
      payload.append("skip_existing", skipExisting);
      payload.append("update_existing", updateExisting);
      payload.append("file", file);
      try {
        const result = await postEmployeeCSV(payload)
          .unwrap()
          .then((res) => {
            console.log(res, "res");
            toast.success("Record added successfully");
            // Close modal after submission
            handleModalClose();
          });
      } catch (error) {
        toast.error(`Something went wrong!!`);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    // Reset the file input
    const fileInput = document.getElementById("csv-file-upload");
    if (fileInput) fileInput.value = "";
  };

  return (
    <CustomModal
      isModalOpen={isImportCsv}
      title="Import Employees from CSV"
      handleClose={handleModalClose}
      maxWidth="sm"
      isShowActionButton={false}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", px: 2 }}
          >
            Upload your CSV file containing employee data. Make sure your file
            follows the required format.
          </Typography>

          <Box>
            <input
              accept=".csv"
              style={{ display: "none" }}
              id="csv-file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <Paper
              variant="outlined"
              onDragOver={!file ? handleDragOver : undefined}
              onDrop={!file ? handleDrop : undefined}
              sx={{
                background:
                  theme.palette.mode === "dark"
                    ? "#0e1922 !important"
                    : "#dfe3e8 !important",
                p: 3,
                textAlign: "center",
                bgcolor: file ? "success.light" : "grey.50",
                border: "2px dashed",
                borderColor: file ? "success.main" : "grey.300",
                borderRadius: 2,
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  borderColor: file ? "success.main" : "primary.main",
                  bgcolor: file ? "success.light" : "grey.100",
                },
              }}
            >
              {!file ? (
                <label
                  htmlFor="csv-file-upload"
                  style={{ width: "100%", cursor: "pointer" }}
                >
                  <Stack spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "50%",
                        bgcolor: "primary.lighter",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" color="primary.main">
                      Drag and drop your CSV file here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or
                    </Typography>
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{
                        borderRadius: 2,
                        px: 4,
                      }}
                    >
                      Browse Files
                    </Button>
                  </Stack>
                </label>
              ) : (
                <Stack spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      bgcolor: "white",
                      borderRadius: 2,
                      width: "100%",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      background:
                        theme.palette.mode === "dark"
                          ? "#182734 !important"
                          : "#fff !important",
                    }}
                  >
                    <InsertDriveFileIcon
                      color="success"
                      sx={{ fontSize: 40 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" color="success.dark">
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        File ready for upload
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handleRemoveFile}
                      color="error"
                      size="small"
                      sx={{
                        "& svg": { fontSize: "24px" },
                        "&:hover": {
                          bgcolor: "error.lighter",
                        },
                      }}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 2,
              background:
                theme.palette.mode === "dark"
                  ? "#182734 !important"
                  : "#dfe3e8 !important",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mb: 1.5 }}
            >
              Import Options
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipExisting}
                    onChange={(e) => setSkipExisting(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Skip existing employees
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Update existing employees
                  </Typography>
                }
              />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              borderTop: 1,
              borderColor: "divider",
              pt: 3,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!file}
              sx={{
                borderRadius: 2,
                px: 3,
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              Import CSV
            </Button>
          </Box>
        </Stack>
      </Box>
    </CustomModal>
  );
};

export default ImportCsv;
